import React, { useEffect, useRef } from 'react';
import clsx from 'clsx';
import configuration from '@/configuration';
import { useLocalizedLinks } from '@/components/core/hooks';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { Element, Icon, Scrollable, JsonLD } from '../../atoms';
import styles from './breadcrumbs.styles';
import BreadcrumbsItem from './components/item';

/**
 *
 * @type {import('./').BreadcrumbType}
 */
const Breadcrumbs = ({ children, className, skipJsonLD, host = '', ...props }) => {
  const ref = useRef(null);
  const links = useLocalizedLinks({ host: true });
  const { i18n } = useLingui();

  const { items, ldjson } = React.Children.toArray(children).reduce(
    (acc, child, i, { length }) => {
      const isLast = i + 1 === length;
      const parsedChildren = React.Children.toArray(child.props.children);
      const isString = parsedChildren.every((c) => {
        return typeof c === 'string' || typeof c === 'number';
      });

      const translation = parsedChildren.every((c) => {
        return c.props?.id && c.props?.message;
      });
      const name =
        child.props.name ||
        (isString ? parsedChildren.join(' ') : undefined) ||
        (translation
          ? parsedChildren
              .map((x) => {
                return i18n._(x.props);
              })
              .join(' ')
          : undefined);

      return {
        items: acc.items.concat([
          <React.Fragment key={child.props.href || child.props.name || i}>
            {React.cloneElement(child, {
              isStatic: child.props.isStatic ?? (!child.props.as && !child.props.href),
            })}
            {!isLast && <Icon className={styles.separator} name="chevron-right" />}
          </React.Fragment>,
        ]),
        ldjson: acc.ldjson.concat([
          {
            '@type': 'ListItem',
            position: i + 2,
            name,
            ...(child.props.href && {
              item: !child.props.href.startsWith('http') ? `${host || configuration.host}${child.props.href}` : child.props.href,
            }),
          },
        ]),
      };
    },
    {
      items: [
        <React.Fragment key="home">
          <Breadcrumbs.Item href={links.root()} name={i18n._(msg`Home`)}>
            <Icon name="home" />
          </Breadcrumbs.Item>
          <Icon className={styles.separator} name="chevron-right" />
        </React.Fragment>,
      ],
      ldjson: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'home',
          item: links.root(),
        },
      ],
    },
  );

  useEffect(() => {
    const { current } = ref;
    current.scrollLeft = current.scrollWidth;
  }, [children]);

  return (
    <Scrollable scrollRef={ref} arrows="hidden" className={clsx(styles.root, className)}>
      <Element as="nav" className={styles.breadcrumbs} {...props}>
        {items}
        {!skipJsonLD && (
          <JsonLD
            value={{
              '@type': 'BreadcrumbList',
              itemListElement: ldjson,
            }}
          />
        )}
      </Element>
    </Scrollable>
  );
};

Breadcrumbs.Item = BreadcrumbsItem;

export default Breadcrumbs;
